import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextField } from '@/components/forms/TextField';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { useSendTestEmail } from './api';

const schema = z.object({
  senderEmailAddress: z
    .string()
    .trim()
    .min(1, 'This field is required.')
    .email('Please enter a valid email address.'),
  targetEmailAddress: z
    .string()
    .trim()
    .min(1, 'This field is required.')
    .email('Please enter a valid email address.'),
  subject: z.string().trim().min(1, 'This field is required.'),
  body: z.string().trim(),
});

type SendTestEmailValues = z.infer<typeof schema>;

// Mirrors the ABP SettingManagement "Send test email" card on the Emailing tab.
export function SendTestEmailCard() {
  const toast = useToast();
  const sendMut = useSendTestEmail();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SendTestEmailValues>({
    resolver: zodResolver(schema),
    defaultValues: { senderEmailAddress: '', targetEmailAddress: '', subject: '', body: '' },
  });

  function onSubmit(values: SendTestEmailValues) {
    sendMut.mutate(values, {
      onSuccess: () => toast.success('The test email has been sent successfully.'),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to send the test email.'),
    });
  }

  return (
    <div className="card mt-4">
      <div className="card-header">
        <h5 className="card-title mb-0">Send test email</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            label="Sender email address"
            type="email"
            autoComplete="off"
            error={errors.senderEmailAddress?.message}
            {...register('senderEmailAddress')}
          />
          <TextField
            label="Target email address"
            type="email"
            autoComplete="off"
            error={errors.targetEmailAddress?.message}
            {...register('targetEmailAddress')}
          />
          <TextField label="Subject" error={errors.subject?.message} {...register('subject')} />

          <div className="mb-3">
            <label htmlFor="SendTestEmail_Body" className="form-label">
              Body
            </label>
            <textarea
              id="SendTestEmail_Body"
              className={`form-control${errors.body ? ' is-invalid' : ''}`}
              rows={4}
              {...register('body')}
            />
            {errors.body && <div className="invalid-feedback">{errors.body.message}</div>}
          </div>

          <button type="submit" className="btn btn-primary" disabled={sendMut.isPending}>
            {sendMut.isPending ? 'Sending…' : 'Send test email'}
          </button>
        </form>
      </div>
    </div>
  );
}
